/*
 * ct-eg-rules.js
 *
 * Eén leesvenster op de regels van EG Auto Add to Cart (vendor 506). De app
 * zet al zijn ACTIEVE regels als JSON in <meta id="easygift-rules">, maar
 * pas NA het laden van de pagina (het app-embed rendert de meta via script).
 * Iedereen die de regels nodig heeft (ct-gift-picker.js, ct-gift-ladder.js)
 * leest ze via dit bestand, zodat er maar één plek is die het ongedocumenteerde
 * formaat van de app kent. Verandert de app zijn JSON, dan breekt alles hier
 * tegelijk en zichtbaar, in plaats van op twee plekken half.
 *
 * Formaat (afgelezen 24-9-2026, app-versie 3.0):
 *   rule.active, rule.name, rule.schedule{enabled,starts,ends}
 *   rule.trigger.type: 'collection' | 'cartValue' | ...
 *   rule.trigger.collections[].gid, .condition ('value' | null), .conditionMin (EURO, geen centen)
 *   rule.trigger.minCartValue (EURO) bij type 'cartValue'
 *   rule.action.type: 'addAutomatically' | 'offerToCustomer'
 *   rule.action.products[].variantId            (addAutomatically)
 *   rule.action.products[].{id,title,handle,variants[].id}  (offerToCustomer)
 *   rule.action.popupOptions.rewardQuantity
 *
 * Bewust geen ES module: wordt als <script defer> geladen vóór de picker en
 * de ladder (snippets/preload-js.liquid), zodat window.ctEasyGift er is.
 */
(function () {
  'use strict';

  var META_ID = 'easygift-rules';
  var cacheRaw = null;
  var cacheRules = null;
  var waiters = [];
  var observer = null;

  function read() {
    var meta = document.getElementById(META_ID);
    if (!meta) return null;
    var raw = meta.getAttribute('content') || meta.textContent || '';
    if (!raw) return null;
    if (raw === cacheRaw) return cacheRules;
    try {
      var rules = JSON.parse(raw);
      if (typeof rules === 'string') rules = JSON.parse(rules);
      if (!Array.isArray(rules)) return null;
      cacheRaw = raw;
      cacheRules = rules;
      return rules;
    } catch (e) {
      return null;
    }
  }

  function flush() {
    var rules = read();
    if (!rules) return false;
    var list = waiters;
    waiters = [];
    list.forEach(function (cb) {
      try { cb(rules); } catch (e) { /* een luisteraar mag de rest niet blokkeren */ }
    });
    if (observer) { observer.disconnect(); observer = null; }
    return true;
  }

  /* Roept cb(rules) aan zodra de meta er is; direct als hij er al staat.
     De observer kijkt naar de hele boom omdat niet vastligt waar de app de
     meta aanhangt; hij wordt losgekoppeld zodra de regels gelezen zijn. */
  function ready(cb) {
    var rules = read();
    if (rules) { cb(rules); return; }
    waiters.push(cb);
    if (!observer && window.MutationObserver) {
      observer = new MutationObserver(function () { flush(); });
      observer.observe(document.documentElement, { childList: true, subtree: true });
    }
  }

  function isActive(rule) {
    if (!rule || rule.active === false || rule.testMode === true) return false;
    var s = rule.schedule;
    if (s && s.enabled) {
      var now = Date.now();
      if (s.starts && new Date(s.starts).getTime() > now) return false;
      if (s.ends && new Date(s.ends).getTime() < now) return false;
    }
    return true;
  }

  /* De keuzeregel (offerToCustomer) waarvan de producttitels precies de
     titels uit de popup zijn. Gebruikt door de picker. */
  function ruleForTitles(names) {
    var rules = read();
    if (!rules || !names || !names.length) return null;
    var best = null;
    rules.forEach(function (rule) {
      var a = rule && rule.action;
      if (!a || a.type !== 'offerToCustomer' || !Array.isArray(a.products)) return;
      var titles = a.products.map(function (p) { return (p.title || '').trim(); });
      var hits = names.filter(function (n) { return titles.indexOf(n) !== -1; }).length;
      if (hits && hits === names.length) best = rule;
    });
    return best;
  }

  /* Genormaliseerde treden voor de ladder. Alleen regels die de ladder kan
     tonen: collectie- of cartValue-gebonden, met een bedragdrempel of zonder
     drempel (elk product uit de collectie). Bedragen in CENTEN.
     Regels waarvan de naam met TEST begint worden overgeslagen: die staan
     winkelbreed aan zodra ze in de meta zitten, ook op live. */
  function tiers() {
    var rules = read();
    if (!rules) return [];
    var out = [];
    rules.forEach(function (rule) {
      if (!isActive(rule)) return;
      if (/^test\b/i.test(rule.name || '')) return;
      var t = rule.trigger || {};
      var a = rule.action || {};
      var threshold = null;
      var collections = [];
      if (t.type === 'collection') {
        collections = (t.collections || []).map(function (c) {
          return String((c && c.gid) || '').split('/').pop();
        }).filter(Boolean);
        if (!collections.length) return;
        if (t.condition === 'value' && t.conditionMin != null) {
          threshold = Math.round(Number(t.conditionMin) * 100);
        } else if (t.condition == null && t.conditionMin == null) {
          threshold = 0;
        } else {
          return;
        }
      } else if (t.type === 'cartValue') {
        if (t.minCartValue == null) return;
        threshold = Math.round(Number(t.minCartValue) * 100);
      } else {
        return;
      }
      if (isNaN(threshold)) return;

      var gifts = [];
      var choice = null;
      if (a.type === 'offerToCustomer') {
        choice = {
          count: Math.max(1, parseInt(a.popupOptions && a.popupOptions.rewardQuantity, 10) || 1),
          products: (a.products || []).map(function (p) {
            return { id: p.id ? String(p.id) : null, title: p.title || '', handle: p.handle || '' };
          })
        };
      } else if (a.type === 'addAutomatically') {
        gifts = (a.products || []).map(function (p) {
          return p.variantId ? String(p.variantId) : null;
        }).filter(Boolean);
        if (!gifts.length) return;
      } else {
        return;
      }

      out.push({
        id: rule._id || rule.name,
        name: rule.name || '',
        groupKey: collections.length ? collections.slice().sort().join(',') : 'cart',
        collections: collections,
        threshold: threshold,
        gifts: gifts,
        choice: choice
      });
    });
    return out;
  }

  window.ctEasyGift = {
    read: read,
    ready: ready,
    isActive: isActive,
    ruleForTitles: ruleForTitles,
    tiers: tiers
  };
})();
